import { MoreVertical, Pencil } from 'lucide-react';
import { useState, useEffect, type KeyboardEvent, type MouseEvent } from 'react';
import type { PickemItem } from '../../api/getMyGames';
import { navigateToGameUrl } from '../../utils/lobbyMyGamesNavigation';
import { getMyGamesSportLogoPath } from '../../utils/myGamesSportLogo';
import { appendEditPicksParam } from '../../utils/playUrl';
import { getReservedStatusLabel } from '../../utils/reservedStatusLabel';

interface LobbyPickemCardProps {
  pickem: PickemItem;
  /** Full public URL with token appended (or null if unavailable). */
  publicUrl: string | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

type PickemState = 'upcoming' | 'countdown' | 'live' | 'final';

function mapPickemStatus(status: string | number, gameDate: Date): PickemState {
  const s = String(status).toUpperCase();
  if (s === 'COMPLETE' || s === 'COMPLETED' || s === 'FINAL' || s === '2') return 'final';
  if (s === 'INPLAY' || s === 'LIVE' || s === '1') return 'live';
  const diff = gameDate.getTime() - Date.now();
  if (diff <= 0) return 'live';
  if (diff <= 86_400_000) return 'countdown';
  return 'upcoming';
}

function formatTimeRemaining(date: Date): string {
  const diff = date.getTime() - Date.now();
  if (diff <= 0) return 'Starting soon';
  const totalSec = Math.floor(diff / 1000);
  const days = Math.floor(totalSec / 86400);
  if (days > 0) return `${days} Day${days > 1 ? 's' : ''}`;
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return [String(h).padStart(2, '0'), String(m).padStart(2, '0'), String(s).padStart(2, '0')].join(
    ':'
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function LobbyPickemCard({ pickem, publicUrl }: LobbyPickemCardProps) {
  const title = ((pickem.title_en || pickem.title_fr || "Pick'em Contest") as string).trim();
  const prize = (pickem.prize_text_en || pickem.prize_text_fr || '') as string;
  const sport = pickem.sport as string | undefined;

  const gameDate = pickem.start
    ? new Date(pickem.start as string)
    : pickem.start_utc_timestamp
      ? new Date((pickem.start_utc_timestamp as number) * 1000)
      : new Date();

  const state = mapPickemStatus(pickem.status, gameDate);
  const isLive = state === 'live';
  const isFinal = state === 'final';

  const totalPicks = (pickem.questions as unknown[])?.length ?? 0;
  // "(N Games)" = distinct underlying fixtures, NOT the question/pick count.
  // Prefer the server-computed games_count (DKPLAT-405); fall back to the question
  // count only if it's absent, to avoid showing 0.
  const gamesCount = typeof pickem.games_count === 'number' ? pickem.games_count : totalPicks;
  const correctPicks = pickem.correct_picks as number | undefined;
  const hasSelections = !!pickem.has_entered;
  const totalEntries = pickem.entries as number | undefined;

  // Reserved-seat entry with no submitted picks (DKPLAT-533): show a persistent
  // status label so the user understands why they scored zero.
  //
  // Keyed off the questions array, NOT `open`. The player payload is gated on
  // questions_posted_at (see applyQuestionRevealGate in pickem.controller.ts), so an
  // unrevealed contest arrives with an empty array while `open` stays true — reading
  // `open` here promised "Make Selections Now!" on a contest with nothing to pick.
  const questionsAvailable = totalPicks > 0;
  const showReservedLabel = !!pickem.has_reservation && !hasSelections;
  const reservedLabel = showReservedLabel
    ? getReservedStatusLabel(state, questionsAvailable)
    : null;

  const logoPath =
    getMyGamesSportLogoPath(sport) ?? (pickem.league_logo_url as string | undefined) ?? null;

  const [countdown, setCountdown] = useState(() => formatTimeRemaining(gameDate));

  useEffect(() => {
    if (state !== 'countdown') return;
    const id = window.setInterval(() => setCountdown(formatTimeRemaining(gameDate)), 1000);
    return () => window.clearInterval(id);
  }, [state, gameDate]);

  const startDisplay = (() => {
    if (state === 'countdown') return countdown;
    if (state === 'live') return 'LIVE';
    if (state === 'final') return 'Complete';
    const days = Math.floor((gameDate.getTime() - Date.now()) / 86_400_000);
    if (days > 0) return `${days} Day${days > 1 ? 's' : ''}`;
    return 'Soon';
  })();

  const picksDisplay = (() => {
    if (isFinal && correctPicks !== undefined) return `${correctPicks}/${totalPicks}`;
    if (isFinal) return `-/${totalPicks}`;
    if (isLive && hasSelections && correctPicks !== undefined)
      return `${correctPicks}/${totalPicks} Correct`;
    return String(totalPicks);
  })();

  // The card offers "Edit Picks" while the contest is still open and the player has
  // submitted selections. That whole card is the click target, so the deep link carries
  // `edit=1` and the pick'em app opens on the selections themselves — no summary screen
  // with a second Edit Picks button in between.
  const canEditPicks = (state === 'upcoming' || state === 'countdown') && hasSelections;
  const targetUrl = canEditPicks ? appendEditPicksParam(publicUrl) : publicUrl;

  const handleNavigate = (e?: MouseEvent | KeyboardEvent) => {
    if (!targetUrl) return;
    e?.preventDefault();
    navigateToGameUrl(targetUrl);
  };

  return (
    <a
      href={targetUrl ?? undefined}
      role={publicUrl ? 'link' : undefined}
      tabIndex={publicUrl ? 0 : -1}
      aria-disabled={!publicUrl}
      onClick={handleNavigate}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') handleNavigate(e);
      }}
      className={`block rounded-xl p-4 border border-[#373737] bg-[#242424] transition-all duration-200 ${
        publicUrl
          ? 'hover:ring-2 hover:ring-[#53d337]/30 cursor-pointer active:scale-[0.98]'
          : 'cursor-default pointer-events-none'
      }`}
    >
      {/* Row 1: Logo + Title + Menu */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex-shrink-0 rounded-lg overflow-hidden bg-[#373737] flex items-center justify-center">
            {logoPath ? (
              <img
                src={logoPath}
                alt={`${sport ?? ''} logo`}
                className="w-full h-full object-contain p-1"
              />
            ) : (
              <span className="text-zinc-400 font-medium text-[10px]">Logo</span>
            )}
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="font-semibold text-sm text-white tracking-wide uppercase leading-tight">
              {title}
            </span>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-zinc-500 text-sm font-normal">Start:</span>
              <span
                className={`text-sm font-semibold ${
                  state === 'final' ? 'text-zinc-400' : 'text-[#53d337]'
                }`}
              >
                {startDisplay}
              </span>
              {gamesCount > 0 && (
                <span className="text-zinc-500 text-sm font-normal">
                  ({gamesCount} Game{gamesCount === 1 ? '' : 's'})
                </span>
              )}
            </div>
          </div>
        </div>
        <MoreVertical className="w-5 h-5 text-zinc-400 flex-shrink-0" />
      </div>

      {/* Divider */}
      <div className="border-t-2 border-[#373737]/60 mb-4" />

      {/* Row 2: Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="flex flex-col gap-1 text-left border-r border-[#6F6F6F] pr-4">
          <span className="text-xs text-zinc-400">Entries</span>
          <span className="text-sm font-bold text-white">
            {totalEntries?.toLocaleString() ?? '0'}
          </span>
        </div>
        <div className="flex flex-col gap-1 text-center border-r border-[#6F6F6F] pr-4">
          <span className="text-xs text-zinc-400">Entry</span>
          <span className="text-sm font-bold text-white">
            {pickem.entry_fee ? `$${(pickem.entry_fee as number).toFixed(2)}` : 'FREE'}
          </span>
        </div>
        <div className="flex flex-col gap-1 text-right">
          <span className="text-xs text-zinc-400">Prize Pool</span>
          <span className="text-sm font-bold text-[#53d337]">
            {prize ||
              (pickem.total_prize_amount
                ? `$${(pickem.total_prize_amount as number).toLocaleString()}`
                : '—')}
          </span>
        </div>
      </div>

      {/* Picks tally (live/final) */}
      {(isLive || isFinal) && totalPicks > 0 && (
        <div className="mt-3 text-xs text-zinc-400 text-center">
          Picks: <span className="text-white font-semibold">{picksDisplay}</span>
        </div>
      )}

      {/* Reserved-seat status label (DKPLAT-533) — persistent, no picks submitted.
          State 2 ("Make Selections Now!") is emphasised as the user needs to act. */}
      {reservedLabel && (
        <div className="mt-4 flex justify-center">
          <span
            className={`inline-flex items-center justify-center rounded-full px-4 py-1.5 text-center text-[13px] font-semibold ${
              reservedLabel.emphasised
                ? 'bg-[#53d337] text-black shadow-[0_0_12px_rgba(83,211,55,0.45)]'
                : 'border border-[#373737] bg-[#121212] text-[#ABABAB]'
            }`}
          >
            {reservedLabel.text}
          </span>
        </div>
      )}

      {/* Edit Picks button (upcoming/countdown, user already has selections) */}
      {canEditPicks && publicUrl && (
        <div className="mt-4 flex justify-center">
          <span className="w-[132px] h-[32px] rounded-full border border-border bg-[#121212] text-white flex items-center justify-center gap-2 transition-colors hover:border-[#53d337]">
            <span className="text-[13px] font-semibold">Edit Picks</span>
            <Pencil className="w-4 h-4" />
          </span>
        </div>
      )}
    </a>
  );
}
